import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiGet } from '../ApiServices/ApiServices';
import authServices from '../services/AuthServices';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext({
  status: null,
  loading: true,
  refresh: async () => {},
  showReminderNow: false,
  dismissReminder: () => {},
});

// Cooldown between reminder popups (ms)
const REMINDER_COOLDOWN_MS = 4 * 60 * 60 * 1000; // 4 hours
const LS_LAST_SHOWN = 'subscriptionReminderLastShownAt';
const POLL_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

export const SubscriptionProvider = ({ children }) => {
  const { authState } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReminderNow, setShowReminderNow] = useState(false);
  const pollTimerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    const token = authServices.getToken();
    if (!token) {
      setStatus(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    const res = await ApiGet('/api/auth/subscription-status');
    if (res.success) {
      const data = res.success.data?.data || null;
      setStatus(data);
      setLoading(false);
      return data;
    }
    setLoading(false);
    return null;
  }, []);

  const evaluateReminder = useCallback((data, sourceIsLogin = false) => {
    if (!data || data.exempt) return;
    const days = data.daysRemaining;
    const threshold = data.reminderThreshold ?? 3;
    // Show when expired (hard), or within threshold days.
    const shouldRemind = data.expired || (typeof days === 'number' && days <= threshold);
    if (!shouldRemind) return;

    // Always show immediately after a fresh login, irrespective of cooldown.
    if (sourceIsLogin) {
      setShowReminderNow(true);
      try { localStorage.setItem(LS_LAST_SHOWN, String(Date.now())); } catch {}
      return;
    }

    // Otherwise honour the 4-hour cooldown.
    let lastShown = 0;
    try { lastShown = Number(localStorage.getItem(LS_LAST_SHOWN)) || 0; } catch {}
    if (Date.now() - lastShown >= REMINDER_COOLDOWN_MS) {
      setShowReminderNow(true);
      try { localStorage.setItem(LS_LAST_SHOWN, String(Date.now())); } catch {}
    }
  }, []);

  const refresh = useCallback(async () => {
    const data = await fetchStatus();
    evaluateReminder(data, false);
    return data;
  }, [fetchStatus, evaluateReminder]);

  // Re-fetch whenever auth state changes — covers login, logout, and token
  // refresh. Treat as a "fresh login" when the token has changed since the
  // last time we fetched in this tab.
  useEffect(() => {
    if (authState.loading) return;
    if (!authState.isAuthenticated) {
      setStatus(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const token = authServices.getToken();
      const marker = localStorage.getItem('subscriptionLoginMarkerToken');
      const isFreshLogin = marker !== token;
      if (isFreshLogin) {
        try { localStorage.setItem('subscriptionLoginMarkerToken', token || ''); } catch {}
      }
      const data = await fetchStatus();
      if (cancelled) return;
      evaluateReminder(data, isFreshLogin);
    })();
    return () => { cancelled = true; };
  }, [authState.isAuthenticated, authState.loading, fetchStatus, evaluateReminder]);

  // Background polling — keeps `status` fresh and re-triggers cooldown popups.
  useEffect(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (!authState.isAuthenticated) return;
    pollTimerRef.current = setInterval(async () => {
      const data = await fetchStatus();
      evaluateReminder(data, false);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollTimerRef.current);
  }, [authState.isAuthenticated, fetchStatus, evaluateReminder]);

  // When any API call comes back with 402 SUBSCRIPTION_EXPIRED, the apiClient
  // interceptor fires this event. Re-fetch immediately so the route guard
  // transitions to the expired page.
  useEffect(() => {
    const onExpired = () => { fetchStatus(); };
    window.addEventListener('rms:subscription-expired', onExpired);
    return () => window.removeEventListener('rms:subscription-expired', onExpired);
  }, [fetchStatus]);

  const dismissReminder = useCallback(() => setShowReminderNow(false), []);

  const value = useMemo(() => ({
    status,
    loading,
    refresh,
    showReminderNow,
    dismissReminder,
  }), [status, loading, refresh, showReminderNow, dismissReminder]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);

export default SubscriptionContext;
